import React, { useEffect, useState } from 'react';
import { ShoppingListEntry } from '../models/shopping-list';
import {
  getShoppingList,
  generateShoppingList as apiGenerateShoppingList,
  addMealToShoppingList as apiAddMealToShoppingList,
  addCustomItem as apiAddCustomItem,
  updateShoppingListEntry,
  clearShoppingList as apiClearShoppingList,
} from '../api/shopping-list-api';

export interface ShoppingListState {
  entries: ShoppingListEntry[];
  isLoading: boolean;
  itemCount: number;
  refresh: () => Promise<void>;
  generateFromPlan: (mealPlanId: string, mode: 'add' | 'overwrite') => Promise<void>;
  addMeal: (mealId: string) => Promise<void>;
  addCustomItem: (name: string) => Promise<void>;
  toggleEntry: (entryId: string, checked: boolean) => Promise<void>;
  clearAll: () => Promise<void>;
}

const initialShoppingListState: ShoppingListState = {
  entries: [],
  isLoading: true,
  itemCount: 0,
  refresh: async () => {},
  generateFromPlan: async () => {},
  addMeal: async () => {},
  addCustomItem: async () => {},
  toggleEntry: async () => {},
  clearAll: async () => {},
};

export const ShoppingListContext = React.createContext<ShoppingListState>(initialShoppingListState);

interface ShoppingListContextProviderProps {
  children: React.ReactNode;
}

export const ShoppingListContextProvider = (props: ShoppingListContextProviderProps) => {
  const [entries, setEntries] = useState<ShoppingListEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [itemCount, setItemCount] = useState(0);

  const refresh = async () => {
    try {
      const response = await getShoppingList();
      setEntries(response.entries);
      setItemCount(response.itemCount);
    } finally {
      setIsLoading(false);
    }
  };

  const generateFromPlan = async (mealPlanId: string, mode: 'add' | 'overwrite'): Promise<void> => {
    await apiGenerateShoppingList(mealPlanId, mode);
    await refresh();
  };

  const addMeal = async (mealId: string): Promise<void> => {
    await apiAddMealToShoppingList(mealId);
    await refresh();
  };

  const addCustomItem = async (name: string): Promise<void> => {
    await apiAddCustomItem(name);
    await refresh();
  };

  const toggleEntry = async (entryId: string, checked: boolean): Promise<void> => {
    await updateShoppingListEntry(entryId, checked);
    await refresh();
  };

  const clearAll = async (): Promise<void> => {
    await apiClearShoppingList();
    await refresh();
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <ShoppingListContext.Provider
      value={{
        entries,
        isLoading,
        itemCount,
        refresh,
        generateFromPlan,
        addMeal,
        addCustomItem,
        toggleEntry,
        clearAll,
      }}
    >
      {props.children}
    </ShoppingListContext.Provider>
  );
};

export default ShoppingListContext;
